"use client";

import { useEffect, useLayoutEffect, useRef } from "react";
import type { FieldValues, UseFormClearErrors } from "react-hook-form";
import { useOTPContext } from "@/components/sections/CommonModals/context";

export interface UseLeadFormValidationResetOptions<T extends FieldValues> {
  clearErrors: UseFormClearErrors<T>;
  /** When lead form hides for OTP (OTPContextWrapper). */
  isOTPSent?: boolean;
  /** Two-step home/modal flow — program fields step reveal. */
  showProgramField?: boolean;
  /** Returning-user header swap after successful lead. */
  isHeaderHidden?: boolean;
  clearOnProgramStep?: boolean;
  clearOnHeaderHidden?: boolean;
}

/**
 * Clears stale RHF validation when the lead UI transitions to OTP, a two-step
 * program step, or returning-user header — prevents errors from a prior surface
 * from appearing on a freshly revealed form state.
 *
 * Also registers clearErrors with OTP context so handleSendOTP can wipe errors
 * when OTP UI opens (same turn as setOTPSent).
 */
export function useLeadFormValidationReset<T extends FieldValues>({
  clearErrors,
  isOTPSent,
  showProgramField,
  isHeaderHidden,
  clearOnProgramStep = true,
  clearOnHeaderHidden = true,
}: UseLeadFormValidationResetOptions<T>) {
  const { registerLeadFormClearErrors } = useOTPContext();
  const prevOtpSent = useRef(isOTPSent);
  const prevShowProgram = useRef(showProgramField);
  const prevHeaderHidden = useRef(isHeaderHidden);
  const clearErrorsRef = useRef(clearErrors);
  clearErrorsRef.current = clearErrors;

  // Fresh modal / form mount must not inherit or surface validation errors.
  // Run once — re-running whenever `clearErrors` identity changes wipes
  // on-blur / on-change field errors before the user sees them.
  useLayoutEffect(() => {
    clearErrorsRef.current();
  }, []);

  useEffect(() => {
    if (!registerLeadFormClearErrors) return;
    registerLeadFormClearErrors(() => clearErrors());
    return () => registerLeadFormClearErrors(null);
  }, [clearErrors, registerLeadFormClearErrors]);

  useEffect(() => {
    if (isOTPSent && !prevOtpSent.current) {
      clearErrors();
    }
    prevOtpSent.current = !!isOTPSent;
  }, [isOTPSent, clearErrors]);

  useEffect(() => {
    if (clearOnProgramStep && showProgramField && !prevShowProgram.current) {
      clearErrors();
    }
    prevShowProgram.current = !!showProgramField;
  }, [clearOnProgramStep, showProgramField, clearErrors]);

  useEffect(() => {
    if (clearOnHeaderHidden && isHeaderHidden && !prevHeaderHidden.current) {
      clearErrors();
    }
    prevHeaderHidden.current = !!isHeaderHidden;
  }, [clearOnHeaderHidden, isHeaderHidden, clearErrors]);
}

/**
 * Previously called `trigger()` when two-step program fields appeared, which
 * forced Degree/Program submit-level errors as soon as the step opened (and
 * raced with `useLeadFormValidationReset` clearErrors). Program fields must
 * only validate on explicit Submit — keep this hook as a no-op so existing
 * call sites remain safe without changing import graphs.
 */
export function useRevalidateOnProgramFieldsChange(
  _trigger?: () => void | Promise<boolean>,
  _requireProgramFields?: boolean,
) {
  // Intentionally empty — see JSDoc.
}

/**
 * Same transition clearing for forms that manage OTP locally (no OTPContext).
 */
export function useLocalLeadFormValidationReset<T extends FieldValues>({
  clearErrors,
  isFormHidden,
  showProgramField,
  clearOnProgramStep = true,
}: {
  clearErrors: UseFormClearErrors<T>;
  isFormHidden?: boolean;
  showProgramField?: boolean;
  clearOnProgramStep?: boolean;
}) {
  const prevHidden = useRef(isFormHidden);
  const prevShowProgram = useRef(showProgramField);
  const clearErrorsRef = useRef(clearErrors);
  clearErrorsRef.current = clearErrors;

  useLayoutEffect(() => {
    clearErrorsRef.current();
  }, []);

  useEffect(() => {
    if (isFormHidden && !prevHidden.current) {
      clearErrors();
    }
    prevHidden.current = !!isFormHidden;
  }, [isFormHidden, clearErrors]);

  useEffect(() => {
    if (clearOnProgramStep && showProgramField && !prevShowProgram.current) {
      clearErrors();
    }
    prevShowProgram.current = !!showProgramField;
  }, [clearOnProgramStep, showProgramField, clearErrors]);
}

/**
 * Runs `trigger` after mount only when deps change — skips the initial mount
 * so modals do not validate empty fields on open (popup validation bug + INP).
 */
export function useRevalidateWhenChanged(
  trigger: () => void | Promise<boolean>,
  deps: unknown[],
) {
  const isFirst = useRef(true);
  useEffect(() => {
    if (isFirst.current) {
      isFirst.current = false;
      return;
    }
    void trigger();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}
